import { SIGNATURE_VIEWBOX, parsePathData, strokesToPathData } from "./signature-path.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Label } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/SignaturePad/index.tsx
/**
* Draw-a-signature pad.
*
* Capture is Pointer Events only — one code path covers mouse, finger and
* stylus, which is the entire reason this works cross-platform without
* branching. Points are held in a ref and painted straight to the canvas so
* dragging never triggers a React render; the only state here is the stroke
* count, which just gates the Undo/Clear buttons.
*
* Coordinates are normalised into the fixed 600x200 space so a signature drawn
* here is byte-identical to one drawn on iOS or Android. See
* `docs/FormBuilder-signature-field-contract.md`.
*/
var SignaturePad = ({ className, error, hint, label, onChangeValue, subText, value }) => {
	const canvasRef = useRef(null);
	const strokesRef = useRef([]);
	/** The last value we emitted, so an echoed prop doesn't clobber the canvas. */
	const emittedRef = useRef(void 0);
	const drawingRef = useRef(false);
	const [strokeCount, setStrokeCount] = useState(0);
	/** Repaints every stroke. Called after resize, undo and clear. */
	const redraw = useCallback(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx) return;
		const { height, width } = canvas.getBoundingClientRect();
		if (!width || !height) return;
		const dpr = window.devicePixelRatio || 1;
		canvas.width = Math.round(width * dpr);
		canvas.height = Math.round(height * dpr);
		ctx.setTransform(width * dpr / SIGNATURE_VIEWBOX.w, 0, 0, height * dpr / SIGNATURE_VIEWBOX.h, 0, 0);
		ctx.clearRect(0, 0, SIGNATURE_VIEWBOX.w, SIGNATURE_VIEWBOX.h);
		ctx.strokeStyle = window.getComputedStyle(canvas).color;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.lineWidth = 2;
		strokesRef.current.forEach((stroke) => {
			if (!stroke.length) return;
			ctx.beginPath();
			ctx.moveTo(stroke[0][0], stroke[0][1]);
			stroke.slice(1).forEach(([x, y]) => ctx.lineTo(x, y));
			if (stroke.length === 1) ctx.lineTo(stroke[0][0], stroke[0][1]);
			ctx.stroke();
		});
	}, []);
	const emit = useCallback(() => {
		const pathData = strokesToPathData(strokesRef.current);
		emittedRef.current = pathData;
		setStrokeCount(strokesRef.current.length);
		onChangeValue(pathData);
	}, [onChangeValue]);
	/** Maps a pointer event into the fixed 600x200 coordinate space. */
	const toSignatureSpace = useCallback((event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		return [(event.clientX - rect.left) / rect.width * SIGNATURE_VIEWBOX.w, (event.clientY - rect.top) / rect.height * SIGNATURE_VIEWBOX.h];
	}, []);
	const handlePointerDown = useCallback((event) => {
		if (!event.isPrimary) return;
		event.currentTarget.setPointerCapture(event.pointerId);
		drawingRef.current = true;
		strokesRef.current.push([toSignatureSpace(event)]);
	}, [toSignatureSpace]);
	const handlePointerMove = useCallback((event) => {
		if (!drawingRef.current) return;
		const ctx = canvasRef.current?.getContext("2d");
		const stroke = strokesRef.current[strokesRef.current.length - 1];
		if (!ctx || !stroke) return;
		const point = toSignatureSpace(event);
		const previous = stroke[stroke.length - 1];
		stroke.push(point);
		ctx.beginPath();
		ctx.moveTo(previous[0], previous[1]);
		ctx.lineTo(point[0], point[1]);
		ctx.stroke();
	}, [toSignatureSpace]);
	const handlePointerUp = useCallback((event) => {
		if (!drawingRef.current) return;
		drawingRef.current = false;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
		redraw();
		emit();
	}, [emit, redraw]);
	const handleClear = useCallback(() => {
		strokesRef.current = [];
		redraw();
		emit();
	}, [emit, redraw]);
	const handleUndo = useCallback(() => {
		strokesRef.current.pop();
		redraw();
		emit();
	}, [emit, redraw]);
	useEffect(() => {
		if (drawingRef.current) return;
		if ((value || "") === (emittedRef.current || "")) return;
		strokesRef.current = parsePathData(value || "");
		emittedRef.current = value || "";
		setStrokeCount(strokesRef.current.length);
		redraw();
	}, [redraw, value]);
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const observer = new ResizeObserver(() => redraw());
		observer.observe(canvas);
		return () => observer.disconnect();
	}, [redraw]);
	return /* @__PURE__ */ jsxs("div", {
		className,
		children: [
			(label || hint) && /* @__PURE__ */ jsxs("div", {
				className: "mb-xxs flex justify-between",
				children: [/* @__PURE__ */ jsx("div", { children: label && /* @__PURE__ */ jsx(Label, { children: label }) }), /* @__PURE__ */ jsx("div", { children: hint && /* @__PURE__ */ jsx(Label, { children: hint }) })]
			}),
			subText && /* @__PURE__ */ jsx("div", {
				className: "mb-xxs text-color-subtle",
				children: subText
			}),
			/* @__PURE__ */ jsx("canvas", {
				className: "border-border bg-surface aspect-[3/1] w-full touch-none rounded border",
				onPointerCancel: handlePointerUp,
				onPointerDown: handlePointerDown,
				onPointerMove: handlePointerMove,
				onPointerUp: handlePointerUp,
				ref: canvasRef
			}),
			error && /* @__PURE__ */ jsx("div", {
				className: "text-color-critical mt-xxs text-sm",
				children: error
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "gap-xs mt-xxs flex justify-end",
				children: [/* @__PURE__ */ jsx(Button, {
					disabled: !strokeCount,
					onClick: handleUndo,
					size: "sm",
					subtle: true,
					variant: "neutral",
					children: "Undo"
				}), /* @__PURE__ */ jsx(Button, {
					disabled: !strokeCount,
					onClick: handleClear,
					size: "sm",
					subtle: true,
					variant: "critical",
					children: "Clear"
				})]
			})
		]
	});
};
//#endregion
export { SignaturePad };

//# sourceMappingURL=index.js.map